import type { EntityNameDto } from '@issuemanager/api-client';
import {
  ExtendedAutocomplete,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { useInfiniteIssueEntityNames } from '@issuemanager/client/features/issues';
import { useMemo, useState } from 'react';
import type { MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export type TransformedIssueEntityNameDto = EntityNameDto & {
  id: string;
};

export type IssueEntityNameMultipleSelectorProps = MultipleSelectorProps<
  TransformedIssueEntityNameDto[],
  TransformedIssueEntityNameDto
>;

function issueEntityNameDataTransformer(data: EntityNameDto) {
  return {
    ...data,
    id: data.name,
  };
}

export function IssueEntityNameMultipleSelector(
  props: IssueEntityNameMultipleSelectorProps,
) {
  const { value: valueProp, onChange, enabled: _enabled, ...rest } = props;

  const value = useMemo(
    () => valueProp?.map(issueEntityNameDataTransformer) ?? [],
    [valueProp],
  );

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value);

  const issueEntityNamesQuery = useInfiniteIssueEntityNames({
    params: {
      filter: {
        searchTerm: filterByText,
      },
    },
    enabled: filterByText.length > 0,
  });

  const options = mergePaginatedDataAndValue(
    issueEntityNamesQuery.data?.pages.map((c) =>
      c.data.map(issueEntityNameDataTransformer),
    ) ?? [],
    value,
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    query: issueEntityNamesQuery,
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
  });

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={sortOptionsBySelected(
        options,
        partialValue,
        autocompleteProps.isOptionEqualToValue,
      )}
      getOptionLabel={(option) => String(option.name)}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
      {...rest}
    />
  );
}
